import React from 'react';
import {
  X, Code, ArrowLeft, ArrowRight, Timer, AlertCircle, Activity,
  Terminal, FileJson, Clock, Check, CheckSquare, Download, RotateCcw,
  Lock, Smartphone, Tablet, Monitor, MonitorSmartphone, Menu, Gauge, Sigma
} from 'lucide-react';
import flexcoaLogo from '../../../assets/images/flexcoa-logo-condensed.png';
import ScreenRenderer from '../../Survey/ScreenRenderer';
import { DEVICE_DIMENSIONS } from './constants';
import { collectIrtItems, scoreIrtResponses } from './irtEngine';
import { findFirstMatchingRule, getResponseKey as getResponseKeyShared } from './skipLogic';
import { buildSkipRecordsForBypass, mergeSkipRecords } from '../../../utils/skipValidation';
import { htmlToPlainText } from './utils';

// Safety cap: if a participant revisits the same screen this many times in
// one session (almost always due to a circular skip-logic configuration),
// we stop obeying skip rules that would send them back there and advance
// sequentially instead. Prevents infinite ping-pong between two screens.
const MAX_VISITS_PER_SCREEN = 5;

const PreviewMode = ({
  previewMode,
  screens,
  previewScreenIndex,
  previewResponses,
  setPreviewResponses,
  previewShowDebug,
  setPreviewShowDebug,
  previewCompleted,
  previewStartTime,
  previewNavigationLog,
  previewScreenTimes,
  previewSkipLogMessage,
  previewValidationErrors,
  navigationRules,
  previewNavigationHistory = [],
  previewScreenVisits = {},
  previewFiredRuleIds = [],
  setPreviewFiredRuleIds = () => {},
  setPreviewSkippedQuestions = () => {},
  allowBackNavigation = true,
  exitPreviewMode,
  enterPreviewMode,
  navigatePreviewScreen,
  navigatePreviewBack,
  validateCurrentScreen,
  completePreviewAssessment
}) => {
  if (!previewMode) return null;

  const getResponseKey = getResponseKeyShared;

  const currentPreviewScreen = screens[previewScreenIndex];

  // --- Live IRT engine (GRM · EAP) -----------------------------------------
  // Only assessments whose items carry IRT calibration get the IRT surfaces;
  // for everything else this is an empty array and no IRT UI renders. Scoring
  // is re-run on every render — it's a small quadrature sum, cheap enough to
  // stay real-time as answers change.
  const irtItems = collectIrtItems(screens);
  const irtResult = irtItems.length > 0 ? scoreIrtResponses(irtItems, previewResponses) : null;
  // Map theta on [-4, 4] to a 0-100% track position for the gauge.
  const thetaToPct = (v) => ((Math.max(-4, Math.min(4, v)) + 4) / 8) * 100;
  // The unanswered item with max Fisher information at theta-hat — what a CAT
  // engine would administer next. Display-only: preview navigation stays
  // sequential/skip-logic driven.
  const irtNextBest = irtResult
    ? irtResult.items
        .filter((i) => !i.answered)
        .reduce((best, i) => (!best || i.information > best.information ? i : best), null)
    : null;

  // --- Preview presentation (FECOA-1533) -----------------------------------
  // The preview frames the live canvas inside a premium "browser emulation"
  // shell. The canvas type drives which of the two agreed design systems is
  // used (derived straight from the screen's stored deviceMode — no extra prop
  // needed): a responsive canvas is presented as a fluid document inside a
  // floating card (Design A); any fixed device canvas is rendered full-bleed,
  // device-sized, so it fills the emulated browser (Design C).
  const previewLayout = currentPreviewScreen?.layout || {};
  const isResponsiveCanvas = previewLayout.deviceMode === 'responsive';
  const deviceCategory = DEVICE_DIMENSIONS[previewLayout.deviceMode]?.category || 'desktop';
  // Browser-window width hint per canvas type so a phone canvas reads as a
  // slim mobile browser and a desktop/responsive canvas as a wide one.
  const browserMaxWidth = isResponsiveCanvas
    ? 1200
    : deviceCategory === 'phone'
      ? 460
      : deviceCategory === 'tablet'
        ? 920
        : 1200;
  const DeviceIcon = isResponsiveCanvas
    ? MonitorSmartphone
    : deviceCategory === 'phone'
      ? Smartphone
      : deviceCategory === 'tablet'
        ? Tablet
        : Monitor;
  const deviceLabel = isResponsiveCanvas
    ? 'Responsive'
    : deviceCategory === 'phone'
      ? 'Mobile'
      : deviceCategory === 'tablet'
        ? 'Tablet'
        : 'Desktop';
  // A tasteful faux address derived from the screen name — pure chrome, no
  // routing. Falls back to a generic survey path when unnamed.
  const urlSlug = (currentPreviewScreen?.name || 'survey')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'survey';

  // Mirror ScreenRenderer's required-answer check so the toolbar Next button
  // visually reflects the same disabled state as the canvas button. Without
  // this, clicking the toolbar Next on a required-but-unanswered screen just
  // surfaces a toast — confusing because the button still looks live.
  const currentScreenRequiredAnswered = (() => {
    if (!currentPreviewScreen?.elements) return true;
    const navRules = currentPreviewScreen.navigationRules || {};
    const required = currentPreviewScreen.elements.filter(el =>
      el.type === 'question' &&
      (navRules[String(el.questionId)]?.is_required === true || el.required === true)
    );
    if (required.length === 0) return true;
    return required.every(el => {
      const r = previewResponses[el.questionId];
      return r !== undefined && r !== null && r !== '' && !(Array.isArray(r) && r.length === 0);
    });
  })();

  // Advance to the next screen. Walks every skip-logic rule across every
  // element — not just rules on visited screens — so cumulative / combination
  // rules fire whenever all contributing answers exist, regardless of the
  // participant's path through non-linear skip logic.
  const advanceScreenWithSkipLogic = (responses) => {
    if (!validateCurrentScreen()) return;

    if (previewScreenIndex >= screens.length - 1) {
      completePreviewAssessment();
      return;
    }

    const allElements = screens.flatMap(s => s.elements || []).filter(e => e.type === 'question');

    let nextScreen = previewScreenIndex + 1;
    let skipReason = null;
    let firingRuleId = null;

    const match = findFirstMatchingRule(navigationRules, {
      allElements,
      responses,
      excludeRuleIds: previewFiredRuleIds,
    });
    if (match) {
      const { rule } = match;
      // Mark this rule as fired so it doesn't re-trigger on subsequent
      // Next clicks. Without this, a cumulative rule whose inputs persist
      // would re-fire every screen after its first match, blocking chained
      // skips and trapping the participant. Back navigation un-fires the
      // rule that caused the transition (see navigatePreviewBack), so a
      // fresh forward pass re-evaluates it.
      setPreviewFiredRuleIds(prev => (prev.includes(rule.id) ? prev : [...prev, rule.id]));
      firingRuleId = rule.id || null;
      if (rule.action_type === 'skip_to') {
        let targetIdx = screens.findIndex(s => String(s.id) === String(rule.target_screen_id));
        if (targetIdx === -1 && rule.target_screen_name) {
          targetIdx = screens.findIndex(s => s.name === rule.target_screen_name);
        }
        if (targetIdx === -1 && rule.target_screen_index !== undefined) {
          targetIdx = rule.target_screen_index;
        }
        if (targetIdx !== -1 && targetIdx < screens.length) {
          if (targetIdx === previewScreenIndex) {
            // Rule's target is the current screen — firing would just pin
            // the participant in place. Advance sequentially instead.
          } else if ((previewScreenVisits[targetIdx] || 0) >= MAX_VISITS_PER_SCREEN) {
            // Loop guard: this screen has already been visited too many
            // times in this session — the rule is almost certainly part
            // of a cycle. Advance sequentially instead of obeying the skip.
            console.warn(
              `[Preview] Skip loop detected — rule would send participant back to "${screens[targetIdx]?.name}" for the ${(previewScreenVisits[targetIdx] || 0) + 1}th time. Ignoring skip, advancing sequentially.`
            );
            skipReason = `Loop prevention — ignored skip to "${screens[targetIdx]?.name}"`;
          } else {
            nextScreen = targetIdx;
            skipReason = `Skip to "${screens[targetIdx]?.name || `screen ${targetIdx + 1}`}"`;
          }
        }
      } else if (rule.action_type === 'end_survey') {
        nextScreen = screens.length;
        skipReason = 'End Assessment';
        // Every screen after the current one is bypassed by end_survey — record
        // their question IDs so required checks at completion honour the skip.
        const endBypass = buildSkipRecordsForBypass(
          previewScreenIndex, screens.length, screens, rule.id || null
        );
        if (endBypass.length > 0) {
          setPreviewSkippedQuestions(prev => mergeSkipRecords(prev, endBypass));
        }
      } else if (rule.action_type === 'show_alert') {
        alert(`[Preview Alert]\n\n${rule.alert_message || 'Alert triggered.'}`);
      }
    }

    if (nextScreen < screens.length) {
      // firingRuleId is forwarded so the hook can record bypassed question
      // IDs against this specific rule when the skip jumps over screens.
      navigatePreviewScreen(nextScreen, skipReason, firingRuleId);
    } else {
      completePreviewAssessment();
    }
  };

  // Bridge ScreenRenderer's navigation callback to PreviewMode's nav primitives.
  // Inline Back/Next buttons inside the screen reach us through this.
  const handleScreenNavigate = (actionType, responses) => {
    if (actionType === 'back') {
      if (allowBackNavigation && previewNavigationHistory.length > 0) {
        navigatePreviewBack();
      }
      return;
    }
    // A canvas Submit button ends the assessment from wherever the participant
    // is — they don't have to be on the last screen-index. Force the
    // submit-time required check so skipping past required questions still
    // blocks finish.
    if (actionType === 'submit') {
      if (!validateCurrentScreen({ isFinish: true })) return;
      completePreviewAssessment();
      return;
    }
    advanceScreenWithSkipLogic(responses);
  };

  const handleScreenResponseChange = (questionId, value) => {
    setPreviewResponses({ ...previewResponses, [questionId]: value });
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-gray-50 force-light-mode">
      {/* Preview Header */}
      <div className="relative z-10 flex h-14 flex-shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="text-sm font-semibold text-gray-900">
            Preview
          </span>
          <span className="hidden items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium tabular-nums text-gray-600 ring-1 ring-inset ring-gray-500/10 sm:inline-flex">
            Screen {previewScreenIndex + 1} of {screens.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPreviewShowDebug(!previewShowDebug)}
            className={`btn btn-sm border shadow-xs ${
              previewShowDebug
                ? 'border-gray-300 bg-gray-100 text-gray-900 hover:bg-gray-200'
                : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900'
            }`}
            title="Toggle Debug Panel"
          >
            <Code className="h-3.5 w-3.5" />
            Debug
          </button>
          <button
            onClick={exitPreviewMode}
            className="btn btn-sm border border-gray-200 bg-white text-gray-700 shadow-xs hover:bg-gray-50 hover:text-gray-900"
          >
            <X className="h-3.5 w-3.5" />
            Exit
          </button>
        </div>
      </div>

      {/* Preview Stage hosting the browser emulation. The live canvas is
           delegated to ScreenRenderer so preview stays pixel-identical to what
           participants see in SurveyTaking. */}
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-gray-100 px-4 py-4 sm:px-6 sm:py-5"
      >
        {currentPreviewScreen ? (
          <div
            // h-full gives the window a definite height that does NOT depend on
            // its content. ScreenRenderer measures this (via flex-1 + min-h-0),
            // so the scale-to-fit math has a stable target — without it the
            // canvas size feeds back into the container size and oscillates
            // forever (FECOA-1533). A responsive canvas expands to the full
            // stage width; fixed canvases keep a device-appropriate max width.
            className="relative z-[1] flex h-full w-full flex-col overflow-hidden rounded-xl bg-white shadow-float ring-1 ring-gray-900/10"
            style={{
              maxWidth: isResponsiveCanvas ? 'none' : `${browserMaxWidth}px`
            }}
          >
            {/* Browser chrome: window dots, address bar and device hint */}
            <div className="flex h-11 flex-shrink-0 items-center gap-3 border-b border-gray-200 bg-gray-50 px-4">
              <div className="flex flex-shrink-0 items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-gray-300" />
              </div>
              <div className="flex min-w-0 flex-1 justify-center">
                <div className="flex h-7 w-full max-w-[440px] items-center gap-2 rounded-md border border-gray-200 bg-white px-3">
                  <Lock className="h-3 w-3 flex-shrink-0 text-gray-400" />
                  <span className="truncate text-xs leading-none">
                    <span className="text-gray-400">flexcoa.com</span>
                    <span className="text-gray-600">/s/{urlSlug}</span>
                  </span>
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-1.5 text-[11px] font-medium text-gray-400">
                <DeviceIcon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{deviceLabel}</span>
              </div>
            </div>

            {/* Participant app banner — a faithful, static replica of the fixed
                flexCOA header (HomeNavMenu) a real participant sees above the
                survey. Rendering it inside the emulated browser makes the
                preview the true end-to-end view for every canvas type. Pure
                chrome: no auth, routing or menu behaviour. */}
            <div className="flex h-14 flex-shrink-0 items-center justify-between bg-primary-600 px-4 sm:px-6">
              <img src={flexcoaLogo} alt="FlexCOA" className="h-7 w-auto sm:h-8" />
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full text-white/90">
                <Menu className="h-5 w-5" />
              </span>
            </div>

            {/* Canvas body — Design A (responsive: contain-fit, fills the page)
                vs Design C (fixed: device-sized, centred). The page surface is
                a soft neutral so the fitted canvas reads as content on a page. */}
            <ScreenRenderer
              screenData={currentPreviewScreen}
              responses={previewResponses}
              onResponseChange={handleScreenResponseChange}
              onNavigate={handleScreenNavigate}
              validationErrors={previewValidationErrors}
              isFirstQuestion={previewScreenIndex === 0}
              allowBackNavigation={allowBackNavigation}
              viewportClassName={
                isResponsiveCanvas
                  ? 'flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden bg-white'
                  : 'flex min-h-0 w-full flex-1 items-center justify-center overflow-auto bg-white'
              }
              canvasClassName="overflow-hidden bg-white"
            />
          </div>
        ) : (
          <div className="relative z-[1] text-sm text-gray-400">No screen to preview</div>
        )}
      </div>

      {/* Live IRT Engine panel — only for assessments with IRT-calibrated
          items. Mirrors the Adaptest conventions (theta on a standard-normal
          scale, T-score = theta x 10 + 50) so preview numbers read the same
          as real scoring output. */}
      {irtItems.length > 0 && !previewCompleted && (
        <div className="fixed bottom-24 left-6 z-40 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-float">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2.5">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Gauge className="h-4 w-4 text-gray-400" />
              IRT Engine
            </div>
            <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-medium text-gray-600">
              GRM · EAP
            </span>
          </div>
          {irtResult ? (
            <div className="space-y-3 px-4 py-3">
              <div className="flex items-baseline justify-between">
                <div>
                  <div className="text-2xl font-semibold tabular-nums text-gray-900">
                    {irtResult.tScore.toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500">
                    T-Score
                  </div>
                </div>
                <div className="text-right text-xs tabular-nums text-gray-500">
                  <div>θ {irtResult.theta.toFixed(2)} ± {irtResult.sem.toFixed(2)}</div>
                  <div className="mt-0.5 text-gray-400">
                    {irtResult.answeredCount} of {irtResult.totalCount} IRT items
                  </div>
                </div>
              </div>
              {/* Theta gauge: confidence band (±1 SEM) + point estimate */}
              <div>
                <div className="relative h-2 rounded-full bg-gray-100">
                  <div
                    className="absolute top-0 h-full rounded-full bg-primary-100"
                    style={{
                      left: `${thetaToPct(irtResult.theta - irtResult.sem)}%`,
                      width: `${thetaToPct(irtResult.theta + irtResult.sem) - thetaToPct(irtResult.theta - irtResult.sem)}%`
                    }}
                  />
                  <div
                    className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-600 ring-2 ring-white"
                    style={{ left: `${thetaToPct(irtResult.theta)}%` }}
                  />
                </div>
                <div className="mt-1 flex justify-between text-[10px] tabular-nums text-gray-400">
                  <span>θ −4</span>
                  <span>0</span>
                  <span>+4</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 text-xs text-gray-500">
              {irtItems.length} IRT-calibrated item{irtItems.length === 1 ? '' : 's'} in this
              assessment. Answer one to begin live theta estimation.
            </div>
          )}
        </div>
      )}

      {/* Skip Logic Toast Notification */}
      {previewSkipLogMessage && (
        <div className="fixed top-24 right-6 z-50 max-w-md rounded-xl border border-gray-200 bg-white px-4 py-3 text-gray-900 shadow-float animate-fade-in">
          <div className="flex items-start gap-3">
            <Activity className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
            <div>
              <div className="mb-0.5 text-sm font-semibold">Skip Logic Triggered</div>
              <div className="text-sm text-gray-600">{previewSkipLogMessage.message}</div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors Toast */}
      {Object.keys(previewValidationErrors).length > 0 && (
        <div className="fixed top-24 right-6 z-50 max-w-md rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-800 shadow-float">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" />
            <div>
              <div className="mb-0.5 text-sm font-semibold">Validation Error</div>
              <div className="text-sm text-red-700">
                {Object.values(previewValidationErrors)[0]}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Controls Bar */}
      {!previewCompleted && (
        <div className="relative z-10 flex h-16 flex-shrink-0 items-center justify-between gap-4 border-t border-gray-200 bg-white px-5">
          <button
            onClick={() => {
              // Use the dedicated back navigation helper so the history stack
              // is correctly maintained and forward history is not clobbered.
              // Never navigate past the first visited screen.
              if (!allowBackNavigation || previewNavigationHistory.length === 0) {
                return;
              }
              navigatePreviewBack();
            }}
            disabled={!allowBackNavigation || previewNavigationHistory.length === 0}
            className="btn border border-gray-200 bg-white text-gray-700 shadow-xs hover:bg-gray-50 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="flex min-w-0 items-center gap-4 text-sm text-gray-500">
            <div className="hidden items-center gap-1.5 tabular-nums sm:flex">
              <Timer className="h-4 w-4 text-gray-400" />
              {Math.floor((Date.now() - previewStartTime) / 1000)}s
            </div>
            <div className="h-1.5 w-32 overflow-hidden rounded-full bg-gray-200 sm:w-48">
              <div
                className="h-full rounded-full bg-primary-600 transition-all duration-300"
                style={{ width: `${((previewScreenIndex + 1) / screens.length) * 100}%` }}
              />
            </div>
            <span className="hidden tabular-nums sm:inline">{Object.keys(previewResponses).length} responses</span>
          </div>

          <button
            onClick={() => advanceScreenWithSkipLogic(previewResponses)}
            disabled={!currentScreenRequiredAnswered}
            className="btn btn-primary disabled:bg-gray-200 disabled:text-gray-500 disabled:opacity-100 disabled:shadow-none"
          >
            {previewScreenIndex < screens.length - 1 ? 'Next' : 'Finish'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Debug Panel */}
      {previewShowDebug && !previewCompleted && (
        <div className="fixed right-6 top-24 bottom-24 w-[34rem] max-w-[calc(100vw-3rem)] bg-gray-800 border border-gray-700 rounded-xl shadow-overlay overflow-hidden flex flex-col z-50">
          <div className="bg-gray-900 px-4 py-3 border-b border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-white font-semibold">
              <Terminal className="w-4 h-4 text-gray-400" />
              Debug Panel
            </div>
            <button
              onClick={() => setPreviewShowDebug(false)}
              className="rounded-md p-1 text-gray-400 hover:bg-gray-800 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-4 text-xs font-mono">
            {/* Current Responses */}
            <div>
              <div className="text-gray-200 font-semibold mb-2 flex items-center gap-2">
                <FileJson className="w-3 h-3 text-gray-400" />
                Current Responses
              </div>
              <pre className="bg-gray-900 p-3 rounded text-gray-300 overflow-x-auto">
                {JSON.stringify(previewResponses, null, 2)}
              </pre>
            </div>
            
            {/* IRT Engine */}
            {irtItems.length > 0 && (
              <div>
                <div className="text-gray-200 font-semibold mb-2 flex items-center gap-2">
                  <Sigma className="w-3 h-3 text-gray-400" />
                  IRT Engine (GRM · EAP)
                </div>
                <div className="bg-gray-900 p-3 rounded space-y-3">
                  {irtResult ? (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="bg-gray-800 rounded p-2.5">
                          <div className="text-gray-100 font-semibold text-base tabular-nums">{irtResult.theta.toFixed(3)}</div>
                          <div className="text-gray-500 text-[11px] mt-0.5">θ (EAP)</div>
                        </div>
                        <div className="bg-gray-800 rounded p-2.5">
                          <div className="text-gray-100 font-semibold text-base tabular-nums">{irtResult.sem.toFixed(3)}</div>
                          <div className="text-gray-500 text-[11px] mt-0.5">SEM</div>
                        </div>
                        <div className="bg-gray-800 rounded p-2.5">
                          <div className="text-gray-100 font-semibold text-base tabular-nums">{irtResult.tScore.toFixed(1)}</div>
                          <div className="text-gray-500 text-[11px] mt-0.5">T-Score</div>
                        </div>
                      </div>
                      <div className="space-y-0.5 text-gray-400">
                        <div className="flex justify-between">
                          <span>IRT items answered</span>
                          <span className="text-gray-300">{irtResult.answeredCount} / {irtResult.totalCount}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Test information at θ̂</span>
                          <span className="text-gray-300">{irtResult.testInformation.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-500">
                      {irtItems.length} IRT item{irtItems.length === 1 ? '' : 's'} in pool — no
                      calibrated responses yet.
                    </div>
                  )}
                  <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                    {(irtResult?.items || irtItems).map((item, idx) => {
                      const isNextBest = irtNextBest && item.responseKey === irtNextBest.responseKey;
                      return (
                        <div
                          key={`${item.responseKey}_${idx}`}
                          className={`rounded-md px-2.5 py-1.5 ${
                            item.answered
                              ? 'bg-gray-800/80 text-gray-200'
                              : isNextBest
                                ? 'bg-amber-900/20 text-gray-300 ring-1 ring-inset ring-amber-500/40'
                                : 'bg-gray-800/30 text-gray-500'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="truncate font-semibold text-[12px]">
                              {item.answered && <span className="text-emerald-400">● </span>}
                              {item.code || item.responseKey}
                            </span>
                            <span className="flex-shrink-0 tabular-nums text-[11px]">
                              {isNextBest && (
                                <span className="mr-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-amber-300">CAT next</span>
                              )}
                              {item.answered ? (
                                <span className="text-emerald-300">cat {item.categoryIndex} · I {item.information.toFixed(2)}</span>
                              ) : (
                                item.information !== undefined && (
                                  <span className="text-gray-400">I {item.information.toFixed(2)}</span>
                                )
                              )}
                            </span>
                          </div>
                          <div className="mt-0.5 text-[11px] tabular-nums text-gray-400">
                            a = {item.slope.toFixed(2)}&ensp;·&ensp;b = [{item.thresholds.map((t) => t.toFixed(2)).join(', ')}]
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Log */}
            <div>
              <div className="text-gray-200 font-semibold mb-2 flex items-center gap-2">
                <Activity className="w-3 h-3 text-gray-400" />
                Navigation & Skip Logic Log
              </div>
              <div className="bg-gray-900 p-3 rounded space-y-2 max-h-96 overflow-y-auto">
                {previewNavigationLog.slice().reverse().map((log, idx) => (
                  <div key={idx} className="text-gray-300 pb-2 border-b border-gray-800 last:border-0">
                    <div className="text-gray-500 text-[10px]">
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </div>
                    <div className="text-[11px]">
                      {log.action === 'started' && <span className="text-green-400">Started: {log.screen}</span>}
                      {log.action === 'navigation' && <span>{log.from} → {log.to}</span>}
                      {log.action === 'skip_logic' && (
                        <>
                          <div className="text-gray-200">Skip Logic</div>
                          <div className="text-gray-400 ml-4">{log.skipReason}</div>
                        </>
                      )}
                      {log.action === 'skip_evaluation' && (
                        <div className="space-y-1">
                          <div className="text-yellow-400 font-semibold">Skip Logic Evaluation: {log.screen}</div>
                          {log.debugLogs?.map((dl, dlIdx) => (
                            <div key={dlIdx} className={`ml-2 ${
                              dl.type === 'info' ? 'text-gray-400' :
                              dl.type === 'question' ? 'text-gray-200' :
                              dl.type === 'rule_match' ? 'text-green-400' :
                              dl.type === 'rule_fail' ? 'text-red-400' :
                              dl.type === 'rule_skip' ? 'text-gray-500' :
                              dl.type === 'action' ? 'text-yellow-300 font-semibold' :
                              dl.type === 'error' ? 'text-red-500' :
                              'text-gray-400'
                            }`}>
                              {dl.type === 'question' && (
                                <div>
                                  <span className="text-gray-100">{dl.msg}</span>
                                  <span className="text-gray-400 ml-2">Answer: </span>
                                  <span className="text-white">{dl.answer}</span>
                                  <span className="text-gray-500 ml-2">({dl.rulesCount} rules)</span>
                                </div>
                              )}
                              {dl.type === 'rule_match' && <span>✓ {dl.msg} → {dl.target}</span>}
                              {dl.type === 'rule_fail' && <span>✗ {dl.msg}</span>}
                              {dl.type === 'rule_skip' && <span>○ {dl.msg}</span>}
                              {dl.type === 'action' && <span>{dl.msg}</span>}
                              {dl.type === 'error' && <span>✗ {dl.msg}</span>}
                              {dl.type === 'info' && <span>{dl.msg}</span>}
                            </div>
                          ))}
                          <div className={`ml-2 font-semibold ${log.result?.includes('Skip') ? 'text-yellow-400' : 'text-gray-400'}`}>
                            → Result: {log.result}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Screen Times */}
            <div>
              <div className="text-gray-200 font-semibold mb-2 flex items-center gap-2">
                <Clock className="w-3 h-3 text-gray-400" />
                Time per Screen
              </div>
              <div className="bg-gray-900 p-3 rounded space-y-1">
                {Object.entries(previewScreenTimes).map(([screenIdx, time]) => (
                  <div key={screenIdx} className="flex justify-between text-gray-300">
                    <span>Screen {parseInt(screenIdx) + 1}:</span>
                    <span>{(time / 1000).toFixed(1)}s</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completion Summary Modal */}
      {previewCompleted && (
        <div className="fixed inset-0 bg-gray-950/40 flex items-center justify-center z-[110] p-6">
          <div className="bg-white rounded-2xl shadow-overlay ring-1 ring-gray-900/5 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Check className="w-5 h-5 text-emerald-600" />
                    Assessment Complete!
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">
                    Test run completed successfully
                  </p>
                </div>
                <button
                  onClick={exitPreviewMode}
                  className="-mr-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                >
                  <X className="w-[18px] h-[18px]" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 divide-x divide-gray-200 rounded-xl border border-gray-200 shadow-card">
                <div className="p-4">
                  <div className="text-gray-500 text-sm">Total Time</div>
                  <div className="text-2xl font-semibold tabular-nums text-gray-900 mt-1">
                    {Math.floor((Date.now() - previewStartTime) / 1000)}s
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-gray-500 text-sm">Screens Viewed</div>
                  <div className="text-2xl font-semibold tabular-nums text-gray-900 mt-1">
                    {Object.keys(previewScreenTimes).length}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-gray-500 text-sm">Responses</div>
                  <div className="text-2xl font-semibold tabular-nums text-gray-900 mt-1">
                    {Object.keys(previewResponses).length}
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-gray-500 text-sm">Skip Events</div>
                  <div className="text-2xl font-semibold tabular-nums text-gray-900 mt-1">
                    {previewNavigationLog.filter(l => l.action === 'skip_logic').length}
                  </div>
                </div>
              </div>

              {/* IRT Score Estimate */}
              {irtResult && (
                <div>
                  <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Gauge className="w-4 h-4 text-gray-400" />
                    IRT Score Estimate
                  </h3>
                  <div className="rounded-xl border border-gray-200 p-4 shadow-card">
                    <div className="grid grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">T-Score</div>
                        <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                          {irtResult.tScore.toFixed(1)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Theta (EAP)</div>
                        <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                          {irtResult.theta.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">SEM</div>
                        <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                          {irtResult.sem.toFixed(2)}
                        </div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">IRT Items</div>
                        <div className="mt-1 text-2xl font-semibold tabular-nums text-gray-900">
                          {irtResult.answeredCount}/{irtResult.totalCount}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-500">
                      Graded Response Model, EAP estimation over the answered IRT items. Preview
                      engine only — live scoring runs through Adaptest.
                    </div>
                  </div>
                </div>
              )}

              {/* Response Summary */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <CheckSquare className="w-4 h-4 text-gray-400" />
                  Response Summary
                </h3>
                <div className="rounded-xl border border-gray-200 overflow-hidden shadow-card">
                  <table className="w-full text-[13.5px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Question</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Response</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(previewResponses).map(([responseKey, value]) => {
                        // Find question by questionId (numeric key) or element.id (string key)
                        const question = screens.flatMap(s => s.elements).find(el => 
                          el.questionId == responseKey || el.id == responseKey
                        );
                        
                        if (!question) return null;
                        
                        // Get question text (strip HTML tags + decode entities)
                        const questionText = htmlToPlainText(
                          question.content || question.data?.text || question.itemData?.text || ''
                        );
                        
                        const displayName = questionText.substring(0, 80) + (questionText.length > 80 ? '...' : '');
                        
                        // Handle multi-select arrays
                        const isArray = Array.isArray(value);
                        // Question types like VAS/NRS/numeric store responseOptions as a
                        // config object, not an array — `|| []` would skip the fallback
                        // and a later .find() would crash. Require an array.
                        const pickOptionsArray = (...candidates) => {
                          for (const c of candidates) if (Array.isArray(c)) return c;
                          return [];
                        };
                        const responseOptions = pickOptionsArray(
                          question.responseOptions,
                          question.data?.options,
                          question.itemData?.options,
                        );
                        
                        // Get response label(s)
                        const getResponseLabel = (val) => {
                          const option = responseOptions.find(opt => 
                            (typeof opt === 'object' ? opt.value : opt) == val
                          );
                          if (option) {
                            const label = typeof option === 'string' ? option : (option.label || option);
                            return typeof label === 'string' ? htmlToPlainText(label) : String(label);
                          }
                          return String(val);
                        };
                        
                        const responseDisplay = isArray 
                          ? value.map(v => getResponseLabel(v)).join(', ')
                          : getResponseLabel(value);
                        
                        const valueDisplay = isArray 
                          ? `[${value.join(', ')}]`
                          : String(value);
                        
                        return (
                          <tr key={responseKey} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-900">
                              {displayName}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {responseDisplay}
                            </td>
                            <td className="px-4 py-3 font-mono text-sm text-gray-700">{valueDisplay}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Navigation Path */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-gray-400" />
                  Navigation Path
                </h3>
                <div className="rounded-xl border border-gray-200 p-4 shadow-card">
                  <div className="flex flex-wrap gap-2 items-center">
                    {previewNavigationLog.filter(l => l.action !== 'completed').map((log, idx) => (
                      <React.Fragment key={idx}>
                        {log.action === 'started' && (
                          <div className="rounded-full bg-gray-100 px-2.5 py-0.5 text-[13px] font-medium text-gray-700 ring-1 ring-inset ring-gray-500/10">
                            {log.screen}
                          </div>
                        )}
                        {log.action !== 'started' && (
                          <>
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <div className={`rounded-full px-2.5 py-0.5 text-[13px] font-medium ring-1 ring-inset ${
                              log.action === 'skip_logic'
                                ? 'bg-amber-50 text-amber-800 ring-amber-600/20'
                                : 'bg-gray-100 text-gray-700 ring-gray-500/10'
                            }`}>
                              {log.to}
                            </div>
                          </>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Data Payload */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <FileJson className="w-4 h-4 text-gray-400" />
                  Data Payload
                </h3>
                <div className="bg-gray-900 rounded-xl p-4 overflow-x-auto">
                  <pre className="text-xs text-gray-300 font-mono">
                    {JSON.stringify({
                      responses: Object.entries(previewResponses).reduce((acc, [questionId, value]) => {
                        const element = screens.flatMap(s => s.elements).find(el => el.id === questionId);
                        const key = element?.code || questionId;
                        acc[key] = value;
                        return acc;
                      }, {}),
                      metadata: {
                        totalDuration: Math.floor((Date.now() - previewStartTime) / 1000),
                        screensViewed: Object.keys(previewScreenTimes).length,
                        navigationPath: previewNavigationLog.filter(l => l.action !== 'completed').map(l => l.to || l.screen),
                        skipLogicEvents: previewNavigationLog.filter(l => l.action === 'skip_logic').length
                      }
                    }, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
            
            <div className="border-t border-gray-100 px-6 py-3.5 bg-gray-50 flex items-center justify-between">
              <button
                onClick={() => {
                  // Transform responses to use element codes when available
                  const transformedResponses = Object.entries(previewResponses).reduce((acc, [questionId, value]) => {
                    const element = screens.flatMap(s => s.elements).find(el => el.id === questionId);
                    const key = element?.code || questionId;
                    acc[key] = value;
                    return acc;
                  }, {});
                  const data = JSON.stringify({
                    responses: transformedResponses,
                    navigationLog: previewNavigationLog,
                    screenTimes: previewScreenTimes
                  }, null, 2);
                  const blob = new Blob([data], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `preview-test-${Date.now()}.json`;
                  a.click();
                }}
                className="btn border border-gray-200 bg-white text-gray-700 shadow-xs hover:bg-gray-50 hover:text-gray-900"
              >
                <Download className="w-4 h-4" />
                Export JSON
              </button>

              <div className="flex gap-2">
                <button
                  onClick={enterPreviewMode}
                  className="btn border border-gray-200 bg-white text-gray-700 shadow-xs hover:bg-gray-50 hover:text-gray-900"
                >
                  <RotateCcw className="w-4 h-4" />
                  Try Again
                </button>
                <button
                  onClick={exitPreviewMode}
                  className="btn btn-primary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewMode;
